import React, { useState } from 'react';
interface SlideFiveProps {
  ctaOption: string | null;
  onBack: () => void;
}
interface FormData {
  name: string;
  role: string;
  clinic: string;
  address: string;
  email: string;
  phone: string;
  consent: boolean;
}
export function SlideFive({
  ctaOption,
  onBack
}: SlideFiveProps) {
  const [form, setForm] = useState<FormData>({
    name: '',
    role: '',
    clinic: '',
    address: '',
    email: '',
    phone: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);
  const isWebMeeting = ctaOption === 'web-meeting';
  const heading = isWebMeeting ? 'Book your web meeting' : 'Get your starter material';
  const subheading = isWebMeeting ? "We'll reach out to schedule a short 15-minute call." : "We'll send everything you need to get started.";
  const handleChange = (field: keyof FormData, value: string | boolean) => {
    setForm(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const isValid = form.name.trim() && form.email.trim() && form.clinic.trim() && form.consent;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isValid) return;
    console.log('Form submitted:', {
      ctaOption,
      ...form
    });
    setSubmitted(true);
  };
  if (submitted) {
    return <div className="flex flex-col h-full w-full justify-center items-center animate-fade-in-up" data-id="element-42">
        <div className="w-16 h-16 rounded-full bg-solventum-primary/20 border-2 border-solventum-primary flex items-center justify-center mb-6" data-id="element-43">
          <svg className="w-8 h-8 text-solventum-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} data-id="element-44">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" data-id="element-45" />
          </svg>
        </div>
        <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-3 text-center" data-id="element-46">
          Thank you!
        </h2>
        <p className="text-white/70 font-body text-center text-lg mb-2" data-id="element-47">
          {isWebMeeting ? "We'll be in touch shortly to schedule your meeting." : 'Your material is on its way to your inbox.'}
        </p>
        <p className="text-solventum-secondary font-body text-center text-sm" data-id="element-48">
          We look forward to helping you save time with digital bonding.
        </p>
      </div>;
  }
  return <div className="flex flex-col h-full w-full justify-center animate-fade-in-up" data-id="element-49">
      {/* Back button */}
      <button onClick={onBack} className="self-start mb-4 flex items-center text-solventum-secondary hover:text-white transition-colors duration-200 text-sm font-body group" aria-label="Go back" data-id="element-50">
        <svg className="w-4 h-4 mr-1.5 transition-transform duration-200 group-hover:-translate-x-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} data-id="element-51">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" data-id="element-52" />
        </svg>
        Back
      </button>

      <h2 className="text-2xl md:text-3xl font-extrabold text-white mb-2 text-center" data-id="element-53">
        {heading}
      </h2>
      <p className="text-solventum-secondary font-body text-center mb-8 text-sm" data-id="element-54">
        {subheading}
      </p>

      <form onSubmit={handleSubmit} className="w-full space-y-4" data-id="element-55">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-56">
          <div data-id="element-57">
            <label className="block text-white/70 text-xs font-body mb-1.5" htmlFor="name" data-id="element-58">
              Name <span className="text-solventum-primary" data-id="element-59">*</span>
            </label>
            <input id="name" type="text" required value={form.name} onChange={e => handleChange('name', e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-solventum-primary focus:ring-1 focus:ring-solventum-primary/50 transition-colors" placeholder="Full name" data-id="element-60" />
          </div>
          <div data-id="element-61">
            <label className="block text-white/70 text-xs font-body mb-1.5" htmlFor="role" data-id="element-62">
              Role
            </label>
            <input id="role" type="text" value={form.role} onChange={e => handleChange('role', e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-solventum-primary focus:ring-1 focus:ring-solventum-primary/50 transition-colors" placeholder="e.g. Orthodontist" data-id="element-63" />
          </div>
        </div>

        <div data-id="element-64">
          <label className="block text-white/70 text-xs font-body mb-1.5" htmlFor="clinic" data-id="element-65">
            Clinic <span className="text-solventum-primary" data-id="element-66">*</span>
          </label>
          <input id="clinic" type="text" required value={form.clinic} onChange={e => handleChange('clinic', e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-solventum-primary focus:ring-1 focus:ring-solventum-primary/50 transition-colors" placeholder="Clinic name" data-id="element-67" />
        </div>

        <div data-id="element-68">
          <label className="block text-white/70 text-xs font-body mb-1.5" htmlFor="address" data-id="element-69">
            Address
          </label>
          <input id="address" type="text" value={form.address} onChange={e => handleChange('address', e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-solventum-primary focus:ring-1 focus:ring-solventum-primary/50 transition-colors" placeholder="Clinic address" data-id="element-70" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-71">
          <div data-id="element-72">
            <label className="block text-white/70 text-xs font-body mb-1.5" htmlFor="email" data-id="element-73">
              Email <span className="text-solventum-primary" data-id="element-74">*</span>
            </label>
            <input id="email" type="email" required value={form.email} onChange={e => handleChange('email', e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-solventum-primary focus:ring-1 focus:ring-solventum-primary/50 transition-colors" placeholder="your@email.com" data-id="element-75" />
          </div>
          <div data-id="element-76">
            <label className="block text-white/70 text-xs font-body mb-1.5" htmlFor="phone" data-id="element-77">
              Phone
            </label>
            <input id="phone" type="tel" value={form.phone} onChange={e => handleChange('phone', e.target.value)} className="w-full bg-white/5 border border-white/15 rounded-xl px-4 py-3 text-white font-body text-sm placeholder-white/30 focus:outline-none focus:border-solventum-primary focus:ring-1 focus:ring-solventum-primary/50 transition-colors" placeholder="+47 000 00 000" data-id="element-78" />
          </div>
        </div>

        {/* Consent */}
        <div className="pt-2" data-id="element-79">
          <label className="flex items-start gap-3 cursor-pointer group" data-id="element-80">
            <div className="relative flex-shrink-0 mt-0.5" data-id="element-81">
              <input type="checkbox" checked={form.consent} onChange={e => handleChange('consent', e.target.checked)} className="sr-only peer" required data-id="element-82" />
              <div className="w-5 h-5 rounded border-2 border-white/30 bg-white/5 peer-checked:bg-solventum-primary peer-checked:border-solventum-primary transition-all duration-200 flex items-center justify-center" data-id="element-83">
                {form.consent && <svg className="w-3 h-3 text-solventum-bg" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-84">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" data-id="element-85" />
                  </svg>}
              </div>
            </div>
            <span className="text-white/60 text-xs font-body leading-relaxed group-hover:text-white/80 transition-colors" data-id="element-86">
              I consent to Solventum processing my personal data to respond to
              my request. I understand I can withdraw my consent at any time.
              See our{' '}
              <span className="text-solventum-secondary underline" data-id="element-87">
                Privacy Policy
              </span>{' '}
              for details.
              <span className="text-solventum-primary" data-id="element-88"> *</span>
            </span>
          </label>
        </div>

        {/* Submit */}
        <div className="pt-3" data-id="element-89">
          <button type="submit" disabled={!isValid} className={`w-full font-headline font-extrabold py-4 px-6 rounded-xl transition-all duration-200 text-lg ${isValid ? 'bg-solventum-primary hover:bg-[#04c444] text-solventum-bg shadow-[0_0_15px_rgba(5,221,77,0.2)] cursor-pointer' : 'bg-white/10 text-white/30 cursor-not-allowed'}`} data-id="element-90">
            {isWebMeeting ? 'Request my meeting' : 'Send me the material'}
          </button>
        </div>
      </form>
    </div>;
}